import React, { useEffect, useRef, useState } from "react";
import { PhotoIcon, SwatchIcon, UserIcon } from "@heroicons/react/24/outline";
import SceneVisualPreview from "./SceneVisualPreview";
import RegenerateVisualModal from "./RegenerateVisualModal";
import { serializeSceneForCopy } from "./copySerialization";
import { useToast } from "../../contexts/ToastContext";

/**
 * Split spoken text into a scannable opening phrase and the remainder.
 */
export function splitSpokenText(text) {
  if (!text || typeof text !== "string") return { lead: null, rest: null };
  const trimmed = text.trim();
  if (!trimmed) return { lead: null, rest: null };

  const maxLen = 64;
  const chunk = trimmed.length <= maxLen ? trimmed : trimmed.slice(0, maxLen);
  const punct = /[.!?;:,]/;
  let end = chunk.length;
  for (let i = chunk.length - 1; i >= 0; i--) {
    if (punct.test(chunk[i])) {
      end = i + 1;
      break;
    }
  }

  const lead = trimmed.slice(0, end).trim();
  const rest = trimmed.slice(end).trim();
  if (!rest) return { lead: trimmed, rest: null };
  return { lead, rest };
}

function formatStyleNotes(styleNotes) {
  if (!styleNotes) return null;
  return styleNotes
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" · ");
}

const sectionLabelClass =
  "flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-45";

const SceneItem = ({
  scene,
  index,
  onCopyScene,
  onEditScene,
  isLast = false,
  imageUrl,
  visualStatus,
  onRetryVisual,
  onRegenerateVisual,
}) => {
  const { showToast } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const menuRef = useRef(null);
  const sceneNum = scene.scene_number ?? index + 1;
  const { lead, rest } = splitSpokenText(scene.spoken_text);
  const styleLine = formatStyleNotes(scene.style_notes);
  const visualDescription = scene.visual_description?.trim() || null;

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleEdit = () => onEditScene?.(scene, index);

  const handleCopy = () => {
    const text = serializeSceneForCopy(scene, index);
    navigator.clipboard.writeText(text).then(() => {
      showToast("Copied!");
      onCopyScene?.();
    });
    setMenuOpen(false);
  };

  const handleRegenerate = async (prompt) => {
    await onRegenerateVisual?.(sceneNum, prompt);
  };

  return (
    <li
      className={`group relative flex flex-col gap-3 py-3 sm:flex-row sm:gap-4 sm:py-3.5 ${
        isLast ? "" : "border-b border-theme-secondary border-opacity-25"
      }`}
    >
      <SceneVisualPreview
        scene={scene}
        sceneNumber={sceneNum}
        imageUrl={imageUrl}
        status={visualStatus}
        onRetryVisual={onRetryVisual}
        onRegenerateVisual={onRegenerateVisual ? () => setRegenerateOpen(true) : undefined}
      />

      <div className="relative min-w-0 flex-1">
        <div ref={menuRef} className="absolute right-0 top-0 z-10">
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className={`rounded p-1 text-theme-text opacity-40 hover:bg-theme-secondary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-theme-primary ${
              menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-60 focus:opacity-100"
            }`}
            aria-label="Scene actions"
            aria-expanded={menuOpen}
            aria-haspopup="true"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="5" cy="12" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="19" cy="12" r="1.5" />
            </svg>
          </button>

          {menuOpen ? (
            <div
              className="absolute right-0 top-full z-30 mt-1 min-w-[10rem] rounded-lg border border-theme-secondary bg-theme-main py-1 shadow-lg"
              role="menu"
            >
              <button
                type="button"
                role="menuitem"
                onClick={handleCopy}
                className="block w-full px-3 py-1.5 text-left text-sm text-theme-text hover:bg-theme-secondary focus:outline-none focus:bg-theme-secondary"
              >
                Copy scene
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  handleEdit();
                }}
                className="block w-full px-3 py-1.5 text-left text-sm text-theme-text hover:bg-theme-secondary focus:outline-none focus:bg-theme-secondary"
              >
                Edit scene
              </button>
            </div>
          ) : null}
        </div>

        <div className="pr-8">
          {scene.speaker ? (
            <div className={`${sectionLabelClass} mb-1.5`}>
              <UserIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {scene.speaker}
            </div>
          ) : null}

          {lead ? (
            <p className="text-xl leading-snug text-theme-text">
              &quot;
              <span className="font-bold">{lead}</span>
              {rest ? ` ${rest}` : null}
              &quot;
            </p>
          ) : (
            <p className="text-xl text-theme-text opacity-50">—</p>
          )}

          {visualDescription ? (
            <div className="mt-4">
              <span className={`${sectionLabelClass} mb-1`}>
                <PhotoIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Visuals
              </span>
              <p className="text-sm leading-relaxed text-theme-text opacity-60">{visualDescription}</p>
            </div>
          ) : null}

          {styleLine ? (
            <div className="mt-3">
              <span className={`${sectionLabelClass} mb-1`}>
                <SwatchIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                Style
              </span>
              <p className="text-sm leading-relaxed text-theme-text opacity-60">{styleLine}</p>
            </div>
          ) : null}
        </div>
      </div>

      {regenerateOpen ? (
        <RegenerateVisualModal
          sceneNumber={sceneNum}
          initialPrompt={visualDescription || ""}
          onClose={() => setRegenerateOpen(false)}
          onRegenerate={handleRegenerate}
        />
      ) : null}
    </li>
  );
};

export default SceneItem;
